import React from 'react';
import { Check } from 'lucide-react';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '../ui/dialog';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { Button } from '../ui/button';
import { CATEGORIES } from './taskCategories';

export const AddTaskDialog = ({ open, onOpenChange, form, setForm, onSubmit }) => {
  const cat = form.category;
  const showRideFields = cat === 'rides';
  const showAddress = cat === 'admin' || cat === 'childcare';
  const showMealFields = cat === 'meals';

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="surface-card max-w-sm max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Post a Task</DialogTitle>
          <DialogDescription>What do you need help with?</DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <Input
            placeholder="e.g., Ride to appointment Thursday"
            value={form.title}
            onChange={(e) => setForm({ ...form, title: e.target.value })}
            className="rounded-xl"
            data-testid="task-title-input"
            autoFocus
          />
          <div className="flex flex-wrap gap-2">
            {CATEGORIES.map((c) => (
              <button
                key={c.key}
                onClick={() => setForm({ ...form, category: c.key })}
                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium transition-colors ${
                  form.category === c.key ? 'bg-cyan-600 text-white' : 'bg-slate-100 dark:bg-slate-800 text-foreground hover:bg-slate-200'
                }`}
                data-testid={`category-${c.key}`}
              >
                <c.icon className="w-3 h-3" /> {c.label}
              </button>
            ))}
          </div>

          <div>
            <label className="text-xs font-medium text-muted-foreground mb-1 block">When is this needed?</label>
            <Input
              type="date"
              value={form.due_date}
              onChange={(e) => setForm({ ...form, due_date: e.target.value })}
              className="rounded-xl"
              data-testid="task-date-input"
            />
          </div>

          {showRideFields && (
            <>
              <div>
                <label className="text-xs font-medium text-muted-foreground mb-1 block">Pickup from</label>
                <Input
                  placeholder="e.g., 123 Oak Ave (home)"
                  value={form.pickup}
                  onChange={(e) => setForm({ ...form, pickup: e.target.value })}
                  className="rounded-xl"
                  data-testid="task-pickup-input"
                />
              </div>
              <div>
                <label className="text-xs font-medium text-muted-foreground mb-1 block">Going to</label>
                <Input
                  placeholder="e.g., Dr. Smith, 456 Main St, Suite 200"
                  value={form.dropoff}
                  onChange={(e) => setForm({ ...form, dropoff: e.target.value })}
                  className="rounded-xl"
                  data-testid="task-dropoff-input"
                />
              </div>
              <button
                type="button"
                onClick={() => setForm({ ...form, round_trip: !form.round_trip })}
                className={`flex items-center gap-2 px-3 py-2 rounded-xl border text-sm transition-colors ${
                  form.round_trip ? 'border-cyan-500 bg-cyan-50 dark:bg-cyan-900/20 text-cyan-700' : 'border-slate-200 dark:border-slate-700 text-muted-foreground'
                }`}
                data-testid="task-round-trip"
              >
                <div className={`w-4 h-4 rounded border-2 flex items-center justify-center flex-shrink-0 ${
                  form.round_trip ? 'bg-cyan-600 border-cyan-600' : 'border-slate-300 dark:border-slate-600'
                }`}>
                  {form.round_trip && <Check className="w-3 h-3 text-white" />}
                </div>
                Need a ride back too
              </button>
            </>
          )}

          {showAddress && (
            <div>
              <label className="text-xs font-medium text-muted-foreground mb-1 block">Home address</label>
              <Input
                placeholder="e.g., 456 Oak Ave"
                value={form.address}
                onChange={(e) => setForm({ ...form, address: e.target.value })}
                className="rounded-xl"
                data-testid="task-address-input"
              />
            </div>
          )}

          {showMealFields && (
            <>
              <div>
                <label className="text-xs font-medium text-muted-foreground mb-1 block">What{"'"}s needed & when</label>
                <Textarea
                  placeholder="e.g., Dinner for 4 on Tuesday. No gluten, no dairy."
                  value={form.meal_info}
                  onChange={(e) => setForm({ ...form, meal_info: e.target.value })}
                  className="rounded-xl resize-none"
                  rows={2}
                  data-testid="task-meal-info"
                />
              </div>
              <div>
                <label className="text-xs font-medium text-muted-foreground mb-1 block">Meal train link (optional)</label>
                <Input
                  placeholder="https://mealtrain.com/..."
                  value={form.meal_train_link}
                  onChange={(e) => setForm({ ...form, meal_train_link: e.target.value })}
                  className="rounded-xl"
                  data-testid="task-meal-train-link"
                />
              </div>
            </>
          )}

          <Textarea
            placeholder="Any other details (optional)"
            value={form.description}
            onChange={(e) => setForm({ ...form, description: e.target.value })}
            className="rounded-xl resize-none"
            rows={2}
          />
          <Button
            onClick={onSubmit}
            className="w-full bg-gradient-to-r from-cyan-600 to-emerald-600 hover:from-cyan-700 hover:to-emerald-700 text-white"
            disabled={!form.title.trim()}
            data-testid="submit-task"
          >
            Post Task
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};
